import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, CreditCard, Loader2, Crown, Shield, Zap } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import ShopLayout from '@/components/shop/ShopLayout';
import { toast } from 'sonner';

const SubscriptionPage = () => {
  const { api } = useAuth();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);
  const [billing, setBilling] = useState('monthly');

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const fetch = async () => {
      try {
        const [plansRes, statusRes] = await Promise.all([
          api.get('/subscriptions/plans'),
          api.get('/subscriptions/status'),
        ]);
        setPlans(plansRes.data.plans);
        setStatus(statusRes.data);
      } catch (e) {
        console.error('Failed to load subscription data', e);
      } finally { setLoading(false); }
    };
    fetch();
  }, [api]);

  // Poll payment status if returning from Stripe
  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await api.get(`/subscriptions/checkout/status/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          toast.success('Payment successful! Your subscription is now active.');
          const statusRes = await api.get('/subscriptions/status');
          setStatus(statusRes.data);
          return;
        }
        if (res.data.status === 'expired') {
          toast.error('Payment session expired.');
          return;
        }
        if (attempts < 5) {
          attempts++;
          setTimeout(poll, 2000);
        }
      } catch { /* ignore */ }
    };
    poll();
  }, [sessionId, api]);

  const handleCheckout = async (planId) => {
    setCheckingOut(planId);
    try {
      const res = await api.post('/subscriptions/checkout', {
        plan: planId,
        billing_cycle: billing,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Checkout failed');
      setCheckingOut(null);
    }
  };

  const handleStartTrial = async () => {
    try {
      const res = await api.post('/subscriptions/start-trial');
      toast.success(res.data.message);
      const statusRes = await api.get('/subscriptions/status');
      setStatus(statusRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start trial');
    }
  };

  if (loading) {
    return <ShopLayout><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" /></div></ShopLayout>;
  }

  const isActive = status?.status === 'active' || status?.status === 'trial';

  return (
    <ShopLayout>
      <div className="max-w-4xl mx-auto space-y-8" data-testid="subscription-page">
        {/* Current Status */}
        {status && status.status !== 'none' && (
          <div className={`p-4 border-2 rounded-sm ${
            status.status === 'active' ? 'bg-green-50 border-green-200' :
            status.status === 'trial' ? 'bg-blue-50 border-blue-200' :
            status.status === 'grace' ? 'bg-amber-50 border-amber-200' :
            'bg-red-50 border-red-200'
          }`} data-testid="subscription-status">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-bold text-[#1e3a5f]">
                  {status.status === 'active' && 'Active Subscription'}
                  {status.status === 'trial' && 'Free Trial'}
                  {status.status === 'grace' && 'Trial Expired — Subscribe Now'}
                  {status.status === 'readonly' && 'Read-Only Mode'}
                  {status.status === 'locked' && 'Account Locked'}
                </p>
                <p className="text-sm text-slate-600">
                  {status.lifetime ? 'Lifetime access' :
                   status.days_left !== undefined ? `${status.days_left} days remaining` :
                   status.message}
                </p>
              </div>
              {status.plan && (
                <span className="px-3 py-1 bg-[#1e3a5f] text-white text-sm font-medium">
                  {plans?.[status.plan]?.name || status.plan}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Start Trial */}
        {status?.status === 'none' && (
          <div className="bg-blue-50 border-2 border-blue-200 p-6 text-center" data-testid="start-trial">
            <Crown className="w-10 h-10 mx-auto mb-3 text-blue-500" />
            <h3 className="text-xl font-bold text-[#1e3a5f] mb-2">Start Your 14-Day Free Trial</h3>
            <p className="text-slate-600 mb-4">No credit card required. Full access to all features.</p>
            <button onClick={handleStartTrial}
              className="vf-btn-primary px-8 py-3 text-lg"
              data-testid="start-trial-btn">
              Start Free Trial
            </button>
          </div>
        )}

        {/* Header */}
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#1e3a5f]">Choose Your Plan</h2>
          <p className="text-slate-500 mt-1">VeriFactu-compliant invoicing and POS for your business</p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center">
          <div className="flex border-2 border-slate-200 rounded-sm overflow-hidden">
            <button onClick={() => setBilling('monthly')}
              className={`px-6 py-2.5 text-sm font-medium transition-colors ${billing === 'monthly' ? 'bg-[#1e3a5f] text-white' : 'bg-white text-slate-600'}`}
              data-testid="billing-monthly">Monthly</button>
            <button onClick={() => setBilling('yearly')}
              className={`px-6 py-2.5 text-sm font-medium transition-colors ${billing === 'yearly' ? 'bg-[#1e3a5f] text-white' : 'bg-white text-slate-600'}`}
              data-testid="billing-yearly">
              Yearly <span className="text-[#22c55e] font-bold ml-1">-17%</span>
            </button>
          </div>
        </div>

        {/* Plan Cards */}
        {plans && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.entries(plans).map(([id, plan]) => {
              const price = billing === 'monthly' ? plan.monthly_price : plan.yearly_price;
              const isCurrentPlan = status?.plan === id && isActive;
              return (
                <motion.div key={id}
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                  className={`border-2 p-6 ${id === 'pos' ? 'border-[#22c55e] bg-green-50/30' : 'border-slate-200 bg-white'}`}
                  data-testid={`plan-card-${id}`}>
                  {id === 'pos' && (
                    <span className="inline-block px-3 py-1 bg-[#22c55e] text-white text-xs font-bold mb-3">MOST POPULAR</span>
                  )}
                  <h3 className="text-xl font-bold text-[#1e3a5f]">{plan.name}</h3>
                  <div className="mt-3 mb-4">
                    <span className="text-3xl font-bold text-[#1e3a5f]">{'\u20AC'}{price.toFixed(2)}</span>
                    <span className="text-slate-500 text-sm ml-1">/{billing === 'monthly' ? 'mo' : 'yr'}</span>
                    {billing === 'yearly' && (
                      <p className="text-xs text-[#22c55e] font-medium mt-1">
                        Save {'\u20AC'}{(plan.monthly_price * 12 - plan.yearly_price).toFixed(2)}/year
                      </p>
                    )}
                  </div>
                  <ul className="space-y-2 mb-6">
                    {plan.features.map((f, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                        <Check className="w-4 h-4 text-[#22c55e] flex-shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  {isCurrentPlan ? (
                    <div className="w-full py-3 text-center bg-slate-100 text-slate-500 font-medium text-sm">
                      Current Plan
                    </div>
                  ) : (
                    <button
                      onClick={() => handleCheckout(id)}
                      disabled={!!checkingOut}
                      className={`w-full py-3 font-medium text-sm flex items-center justify-center gap-2 transition-colors ${
                        id === 'pos'
                          ? 'bg-[#22c55e] text-white hover:bg-[#16a34a]'
                          : 'bg-[#1e3a5f] text-white hover:bg-[#2a4f7f]'
                      } disabled:opacity-50`}
                      data-testid={`subscribe-${id}`}>
                      {checkingOut === id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                      Subscribe Now
                    </button>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </ShopLayout>
  );
};

export default SubscriptionPage;
